import { Fragment, useRef, useState } from 'react';
import Ikon from './ikon';
import { adresDenetle, sor } from '../../ui/Sor';

/**
 * ÜRÜN AÇIKLAMASI — sade biçimli metin.
 *
 * Saklama biçimi SADE MARKDOWN'dır ve aynı `description` alanında durur:
 *   **kalın**   *italik*   - madde   1. numaralı   [metin](https://adres)
 * Eski düz metin açıklamalar olduğu gibi okunur (satır sonları korunur); yeni
 * biçim de düz metin olarak okunabilir kalır. HTML saklanmaz ve basılmaz:
 * gösterim React öğeleriyle kurulur, bağlantıda yalnız http(s) kabul edilir.
 */

/* ─── Gösterim ─────────────────────────────────────────────────────────── */
// Geriye bakış (lookbehind) KULLANILMAZ: eski iOS Safari'de tüm betiği düşürür. İtalikte önceki karakter 5. grupta yakalanır.
const SATIR_ICI = /(\*\*([^*\n]+?)\*\*)|(\[([^\]\n]+)\]\((https?:\/\/[^\s)]+)\))|((^|[^\w*])\*([^\s*](?:[^*\n]*?[^\s*])?)\*(?![\w*]))/g;

function satirIci(metin: string, anahtar: string): React.ReactNode[] {
  const parcalar: React.ReactNode[] = [];
  let son = 0; let n = 0;
  for (const m of metin.matchAll(SATIR_ICI)) {
    if (m.index! > son) parcalar.push(metin.slice(son, m.index));
    const k = `${anahtar}-${n++}`;
    if (m[1]) parcalar.push(<strong key={k}>{m[2]}</strong>);
    else if (m[3]) parcalar.push(<a key={k} href={m[5]} target="_blank" rel="noopener noreferrer">{m[4]}</a>);
    else { if (m[7]) parcalar.push(m[7]); parcalar.push(<em key={k}>{m[8]}</em>); }
    son = m.index! + m[0].length;
  }
  if (son < metin.length) parcalar.push(metin.slice(son));
  return parcalar;
}

type Blok = { tur: 'p'; satirlar: string[] } | { tur: 'ul' | 'ol'; maddeler: string[] };
function bloklar(metin: string): Blok[] {
  const cikti: Blok[] = [];
  for (const ham of metin.replace(/\r\n?/g, '\n').split('\n')) {
    const madde = /^\s*[-•]\s+(.*)$/.exec(ham);
    const numara = /^\s*\d{1,3}[.)]\s+(.*)$/.exec(ham);
    const son = cikti[cikti.length - 1];
    if (madde) { if (son?.tur === 'ul') son.maddeler.push(madde[1]); else cikti.push({ tur: 'ul', maddeler: [madde[1]] }); }
    else if (numara) { if (son?.tur === 'ol') son.maddeler.push(numara[1]); else cikti.push({ tur: 'ol', maddeler: [numara[1]] }); }
    else if (ham.trim() === '') cikti.push({ tur: 'p', satirlar: [] });
    else if (son?.tur === 'p' && son.satirlar.length > 0) son.satirlar.push(ham);
    else cikti.push({ tur: 'p', satirlar: [ham] });
  }
  return cikti.filter((b) => b.tur !== 'p' || b.satirlar.length > 0);
}

export function AciklamaGoster({ metin }: { metin: string }) {
  return (
    <div className="ccd-uk-aciklama">
      {bloklar(metin).map((b, i) => {
        if (b.tur === 'p') {
          return <p key={i}>{b.satirlar.map((s, j) => <Fragment key={j}>{j > 0 && <br />}{satirIci(s, `${i}-${j}`)}</Fragment>)}</p>;
        }
        const maddeler = b.maddeler.map((s, j) => <li key={j}>{satirIci(s, `${i}-${j}`)}</li>);
        return b.tur === 'ul' ? <ul key={i}>{maddeler}</ul> : <ol key={i}>{maddeler}</ol>;
      })}
    </div>
  );
}

/** Biçim işaretlerini atıp düz metne indirger ("Temizle" düğmesi). */
export function duzMetin(metin: string): string {
  return metin
    .replace(/\[([^\]\n]+)\]\((https?:\/\/[^\s)]+)\)/g, '$1 ($2)')
    .replace(/\*\*([^*\n]+?)\*\*/g, '$1')
    .replace(/(^|[^\w*])\*([^\s*](?:[^*\n]*?[^\s*])?)\*(?![\w*])/gm, '$1$2')
    .replace(/^\s*[-•]\s+/gm, '')
    .replace(/^\s*\d{1,3}[.)]\s+/gm, '');
}

/* ─── Düzenleyici ──────────────────────────────────────────────────────── */
export const ACIKLAMA_SINIR = 5000;

export default function AciklamaDuzenleyici({
  deger, degis, pasif, hata,
}: { deger: string; degis: (v: string) => void; pasif?: boolean; hata?: string }) {
  const ref = useRef<HTMLTextAreaElement>(null);
  const [onizleme, setOnizleme] = useState(false);

  /** Seçimi verilen işlevle değiştirir ve imleci yeni seçime koyar. */
  function uygula(fn: (secili: string, tum: { bas: number; son: number; metin: string }) => { metin: string; bas: number; son: number }) {
    const el = ref.current;
    if (!el) return;
    const bas = el.selectionStart; const son = el.selectionEnd;
    const r = fn(deger.slice(bas, son), { bas, son, metin: deger });
    degis(r.metin.slice(0, ACIKLAMA_SINIR));
    requestAnimationFrame(() => { el.focus(); el.setSelectionRange(r.bas, r.son); });
  }
  const sar = (isaret: string, ornek: string) => uygula((secili, t) => {
    const ic = secili || ornek;
    const metin = t.metin.slice(0, t.bas) + isaret + ic + isaret + t.metin.slice(t.son);
    return { metin, bas: t.bas + isaret.length, son: t.bas + isaret.length + ic.length };
  });
  /** Seçimin değdiği satırların başına madde / numara koyar; zaten varsa kaldırır. */
  const satirBasi = (numarali: boolean) => uygula((_s, t) => {
    const ilk = t.metin.lastIndexOf('\n', t.bas - 1) + 1;
    const sonIdx = t.metin.indexOf('\n', t.son);
    const bit = sonIdx < 0 ? t.metin.length : sonIdx;
    const satirlar = t.metin.slice(ilk, bit).split('\n');
    const desen = numarali ? /^\s*\d{1,3}[.)]\s+/ : /^\s*[-•]\s+/;
    const hepsiVar = satirlar.every((s) => desen.test(s) || s.trim() === '');
    const yeni = satirlar.map((s, i) => {
      if (s.trim() === '' && satirlar.length > 1) return s;
      const ciplak = s.replace(/^\s*(?:[-•]|\d{1,3}[.)])\s+/, '');
      return hepsiVar ? ciplak : `${numarali ? `${i + 1}.` : '-'} ${ciplak}`;
    }).join('\n');
    return { metin: t.metin.slice(0, ilk) + yeni + t.metin.slice(bit), bas: ilk, son: ilk + yeni.length };
  });
  const baglanti = async () => {
    const adres = await sor({ baslik: 'Bağlantı ekle', etiket: 'Bağlantı adresi', tur: 'adres', yerTutucu: 'https://…', zorunlu: true, azami: 1000, onay: 'Bağlantıyı ekle', denetle: adresDenetle });
    if (!adres) return;
    uygula((secili, t) => {
      const ic = secili || 'bağlantı metni';
      const ek = `[${ic}](${adres.trim()})`;
      return { metin: t.metin.slice(0, t.bas) + ek + t.metin.slice(t.son), bas: t.bas + 1, son: t.bas + 1 + ic.length };
    });
  };
  const temizle = () => uygula((secili, t) => {
    if (!secili) return { metin: duzMetin(t.metin), bas: 0, son: 0 };
    const ic = duzMetin(secili);
    return { metin: t.metin.slice(0, t.bas) + ic + t.metin.slice(t.son), bas: t.bas, son: t.bas + ic.length };
  });

  const dugme = (ad: Parameters<typeof Ikon>[0]['ad'], etiket: string, tikla: () => void) => (
    <button type="button" className="ccd-uk-arac-dugme" title={etiket} aria-label={etiket} disabled={pasif || onizleme} onClick={tikla}>
      <Ikon ad={ad} boy={16} />
    </button>
  );

  return (
    <div className={`ccd-uk-duzenleyici${hata ? ' ccd-uk-hatali' : ''}`}>
      <div className="ccd-uk-arac" role="toolbar" aria-label="Biçim">
        {dugme('kalin', 'Kalın', () => sar('**', 'kalın metin'))}
        {dugme('italik', 'İtalik', () => sar('*', 'italik metin'))}
        <span className="ccd-uk-arac-ayrac" />
        {dugme('liste', 'Madde işaretli liste', () => satirBasi(false))}
        {dugme('numara', 'Numaralı liste', () => satirBasi(true))}
        <span className="ccd-uk-arac-ayrac" />
        {dugme('baglanti', 'Bağlantı ekle', baglanti)}
        {dugme('temizle', 'Biçimi temizle (seçili metin ya da tümü)', temizle)}
        <span className="ccd-uk-arac-bosluk" />
        <button type="button" className={`ccd-uk-arac-metin${onizleme ? ' ccd-is-active' : ''}`} aria-pressed={onizleme}
                onClick={() => setOnizleme((x) => !x)}>
          {onizleme ? 'Düzenle' : 'Önizleme'}
        </button>
      </div>
      {onizleme ? (
        <div className="ccd-uk-onizleme">
          {deger.trim() ? <AciklamaGoster metin={deger} /> : <span className="ccd-muted">Henüz açıklama yazılmadı.</span>}
        </div>
      ) : (
        <textarea
          ref={ref} id="uk-aciklama" className="ccd-uk-metin" rows={8} maxLength={ACIKLAMA_SINIR} value={deger} disabled={pasif}
          placeholder="Ürünü anlatan metin: nerede kullanılır, nasıl döşenir, öne çıkan özelliği…"
          onChange={(e) => degis(e.target.value)}
          onKeyDown={(e) => {
            if (!(e.metaKey || e.ctrlKey)) return;
            if (e.key === 'b') { e.preventDefault(); sar('**', 'kalın metin'); }
            if (e.key === 'i') { e.preventDefault(); sar('*', 'italik metin'); }
          }}
        />
      )}
      <div className="ccd-uk-duzenleyici-alt">
        <span>{hata ?? 'Biçim işaretleri metinle birlikte saklanır; düz metin olarak da okunur.'}</span>
        <span>{deger.length} / {ACIKLAMA_SINIR}</span>
      </div>
    </div>
  );
}
